import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '../components/ui/sheet';
import PayoutConnectCard from '../components/PayoutConnectCard';
import { CURRENCIES, formatMoney } from '../lib/currency';
import {
  GraduationCap, Users, BookOpen, Plus, Eye, BarChart3,
  TrendingUp, CheckCircle2, Clock, PoundSterling, Star,
  ArrowRight, Play, Award, Menu, LogOut, Settings, Upload, Edit, Trash2
} from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'professional_training', label: 'Professional Training' },
  { value: 'tech_bootcamp', label: 'Tech Bootcamp' },
  { value: 'language', label: 'Language' },
  { value: 'creative', label: 'Creative' },
  { value: 'wellness', label: 'Wellness' },
];

export default function CourseProviderDashboard() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (user?.user_type !== 'course_provider') {
      navigate('/');
      return;
    }
    fetchDashboard();
    fetchProfile();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/courses/provider/dashboard');
      setDashboard(res.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/course-providers/my-profile');
      setProfileForm(res.data || {});
      if (res.data?.logo_url) {
        const url = res.data.logo_url;
        setLogoPreview(url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);
      }
    } catch {
      setProfileForm({});
    }
  };

  const saveProfile = async () => {
    if (!profileForm.business_name) {
      toast.error('Business/trainer name is required');
      return;
    }
    setSavingProfile(true);
    try {
      await api.put('/course-providers/my-profile', profileForm);
      toast.success('Profile saved');
      fetchProfile();
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/course-providers/my-profile/logo', formData);
      toast.success('Logo uploaded');
      setLogoPreview(res.data.logo_url);
    } catch {
      toast.error('Failed to upload logo');
    }
  };

  const updateCourseStatus = async (courseId, newStatus) => {
    try {
      await api.put(`/courses/${courseId}`, { status: newStatus });
      toast.success('Course status updated');
      fetchDashboard();
    } catch {
      toast.error('Failed to update course');
    }
  };

  const promoteCourse = async (courseId, tier) => {
    try {
      await api.put(`/courses/${courseId}`, { listing_tier: tier });
      toast.success(`Course promoted to ${tier} tier`);
      fetchDashboard();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('You need to purchase a listing credit for this tier first');
        navigate('/services');
      } else {
        toast.error('Failed to promote course');
      }
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course? This cannot be undone.')) return;
    try {
      await api.delete(`/courses/${courseId}`);
      toast.success('Course deleted');
      fetchDashboard();
    } catch {
      toast.error('Failed to delete course');
    }
  };

  const openEditModal = (course) => {
    setEditForm({
      title: course.title || '',
      description: course.description || '',
      category: course.category || '',
      instructor: course.instructor || '',
      duration: course.duration || '',
      price: course.price ?? '',
      original_price: course.original_price ?? '',
      currency: course.currency || 'GBP',
      delivery_mode: course.delivery_mode || 'online',
      location: course.location || '',
      start_date: course.start_date ? course.start_date.slice(0, 10) : '',
      end_date: course.end_date ? course.end_date.slice(0, 10) : '',
      certification: course.certification || '',
      requirements: (course.requirements || []).join('\n'),
      outcomes: (course.outcomes || []).join('\n'),
      image_url: course.image_url || '',
      video_url: course.video_url || '',
      id: course.id,
    });
    setShowEditModal(true);
  };

  const saveEditedCourse = async () => {
    if (!editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/courses/${editForm.id}`, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        instructor: editForm.instructor,
        duration: editForm.duration,
        price: editForm.price === '' ? null : parseFloat(editForm.price),
        original_price: editForm.original_price === '' ? null : parseFloat(editForm.original_price),
        currency: editForm.currency,
        delivery_mode: editForm.delivery_mode,
        location: editForm.location,
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        certification: editForm.certification,
        requirements: editForm.requirements ? editForm.requirements.split('\n').map(s => s.trim()).filter(Boolean) : [],
        outcomes: editForm.outcomes ? editForm.outcomes.split('\n').map(s => s.trim()).filter(Boolean) : [],
        image_url: editForm.image_url,
        video_url: editForm.video_url,
      });
      toast.success('Course updated');
      setShowEditModal(false);
      fetchDashboard();
    } catch {
      toast.error('Failed to update course');
    } finally {
      setSavingEdit(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'profile', label: 'Profile', icon: Settings },
    { id: 'courses', label: 'My Courses', icon: BookOpen },
    { id: 'enrollments', label: 'Enrollments', icon: Users },
  ];

  const stats = dashboard ? [
    { label: 'Total Courses', value: dashboard.total_courses, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'Active Courses', value: dashboard.active_courses, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Total Enrollments', value: dashboard.total_enrollments, icon: Users, color: 'text-purple-600', bg: 'bg-purple-100' },
    { label: 'Est. Revenue', value: `£${(dashboard.estimated_revenue || 0).toLocaleString()}`, icon: PoundSterling, color: 'text-amber-600', bg: 'bg-amber-100' },
  ] : [];

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-emerald-700 min-h-screen sticky top-0 hidden lg:block">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold">Course Provider</p>
                <p className="text-white/60 text-xs truncate max-w-[140px]">{user?.full_name}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>

            <div className="mt-8 space-y-3">
              <Link to="/courses/post">
                <Button className="w-full bg-white text-emerald-700 hover:bg-white/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Post New Course
                </Button>
              </Link>
              <Link to="/courses">
                <Button variant="ghost" className="w-full text-white/70 hover:text-white hover:bg-white/10">
                  <Eye className="w-4 h-4 mr-2" />
                  View All Courses
                </Button>
              </Link>
              <Button variant="ghost" className="w-full text-white/70 hover:text-white hover:bg-white/10" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-emerald-700 border-0">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-bold text-sm">Course Provider</p>
              </div>
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-8">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 pb-4 border-b">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">
              {TABS.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <div className="max-w-5xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Course Provider Dashboard</h1>
                <p className="text-muted-foreground mt-1">Manage your courses and reach thousands of learners</p>
              </div>
              <Link to="/courses/post" className="hidden md:block">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Course
                </Button>
              </Link>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <PayoutConnectCard />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {loading ? (
                    Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)
                  ) : (
                    stats.map((s, i) => (
                      <Card key={i}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">{s.label}</p>
                              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                            </div>
                            <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                              <s.icon className={`w-6 h-6 ${s.color}`} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {[
                        { label: 'Post a New Course', link: '/courses/post', icon: Plus, color: 'bg-emerald-600' },
                        { label: 'Browse All Courses', link: '/courses', icon: BookOpen, color: 'bg-blue-600' },
                        { label: 'View My Profile', link: '/profile', icon: Award, color: 'bg-purple-600' },
                      ].map((action, i) => (
                        <Link key={i} to={action.link}>
                          <Button variant="outline" className="w-full justify-between hover:border-emerald-600">
                            <span className="flex items-center gap-2">
                              <div className={`w-6 h-6 rounded ${action.color} flex items-center justify-center`}>
                                <action.icon className="w-3 h-3 text-white" />
                              </div>
                              {action.label}
                            </span>
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Enrollments</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loading ? <Skeleton className="h-32" /> : (dashboard?.recent_enrollments || []).length === 0 ? (
                        <div className="text-center py-8">
                          <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground text-sm">No enrollments yet.</p>
                          <p className="text-muted-foreground text-sm">Post courses to attract students!</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {(dashboard?.recent_enrollments || []).slice(0, 5).map((enr, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                              <div>
                                <p className="text-sm font-medium">Student enrolled</p>
                                <p className="text-xs text-muted-foreground">{enr.enrolled_at?.slice(0, 10)}</p>
                              </div>
                              <Badge className={enr.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                                {enr.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Income Stream Info */}
                <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-2">Grow Your Reach</h3>
                        <p className="text-white/80">
                          Your courses are available to over 26 million candidates.
                          Each enrollment generates revenue for your institution.
                        </p>
                        <div className="mt-3 flex gap-4 text-sm text-white/80">
                          <span><CheckCircle2 className="w-4 h-4 inline mr-1" />Global audience</span>
                          <span><CheckCircle2 className="w-4 h-4 inline mr-1" />Direct enrollment</span>
                          <span><CheckCircle2 className="w-4 h-4 inline mr-1" />No setup fees</span>
                        </div>
                      </div>
                      <Link to="/courses/post">
                        <Button className="bg-white text-emerald-600 hover:bg-white/90 shrink-0">
                          Post a Course
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Provider Profile</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Business/Trainer Name</Label>
                        <Input
                          value={profileForm.business_name || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })}
                          placeholder="Your training business name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <select
                          value={profileForm.category || 'professional_training'}
                          onChange={(e) => setProfileForm({ ...profileForm, category: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                        >
                          {CATEGORY_OPTIONS.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={profileForm.website || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                          placeholder="https://www.example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          value={profileForm.email || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          placeholder="hello@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={profileForm.phone || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                          placeholder="+44 ..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          value={profileForm.location || ''}
                          onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                          placeholder="City, Country"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Expertise (comma-separated)</Label>
                      <Input
                        value={(profileForm.expertise || []).join(', ')}
                        onChange={(e) => setProfileForm({ ...profileForm, expertise: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="Leadership, Excel, Public Speaking"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>About</Label>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 text-sm resize-none outline-none focus:ring-2 focus:ring-emerald-500/20"
                        rows={5}
                        value={profileForm.about || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, about: e.target.value })}
                        placeholder="Tell learners about your training business..."
                      />
                    </div>
                    <Button onClick={saveProfile} disabled={savingProfile} className="bg-emerald-600 hover:bg-emerald-700">
                      {savingProfile ? 'Saving...' : 'Save Profile'}
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle>Logo</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                        ) : (
                          <GraduationCap className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                        <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                          <Upload className="w-3.5 h-3.5 mr-2" />Upload Logo
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">My Courses ({dashboard?.courses?.length || 0})</h2>
                  <Link to="/courses/post">
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />Add Course
                    </Button>
                  </Link>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-48" />)}
                  </div>
                ) : (dashboard?.courses || []).length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No courses yet</h3>
                      <p className="text-muted-foreground mb-6">Start posting courses to reach thousands of learners across the UK.</p>
                      <Link to="/courses/post">
                        <Button className="bg-emerald-600 hover:bg-emerald-700">Post Your First Course</Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(dashboard?.courses || []).map(course => (
                      <Card key={course.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold line-clamp-2">{course.title}</h3>
                              <p className="text-sm text-muted-foreground mt-1">{course.category} • {course.level}</p>
                            </div>
                            <Badge className={`ml-2 shrink-0 ${course.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                              {course.status}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-4">
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />{course.enrollments_count || 0} enrolled
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />{course.duration || 'N/A'}
                            </span>
                            <span className="font-medium text-[#003366]">
                              {course.is_free ? 'Free' : formatMoney(course.price, course.currency)}
                            </span>
                            {course.average_rating > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                {course.average_rating?.toFixed(1)}
                              </span>
                            )}
                            {course.listing_tier && course.listing_tier !== 'free' && (
                              <Badge className="bg-purple-100 text-purple-700 capitalize">{course.listing_tier}</Badge>
                            )}
                          </div>
                          <div className="flex gap-2 mb-2">
                            <Link to={`/courses/${course.id}`} className="flex-1">
                              <Button variant="outline" size="sm" className="w-full">
                                <Eye className="w-3.5 h-3.5 mr-2" />View
                              </Button>
                            </Link>
                            <Button variant="outline" size="sm" onClick={() => openEditModal(course)}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateCourseStatus(course.id, course.status === 'active' ? 'draft' : 'active')}
                              className={course.status === 'active' ? 'text-amber-600 border-amber-300' : 'text-emerald-600 border-emerald-300'}
                            >
                              {course.status === 'active' ? 'Pause' : 'Activate'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => deleteCourse(course.id)} className="text-red-500 hover:text-red-600 border-red-200">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          <select
                            defaultValue=""
                            onChange={(e) => { if (e.target.value) { promoteCourse(course.id, e.target.value); e.target.value = ''; } }}
                            className="w-full border rounded-md px-2 py-1.5 text-xs text-purple-700 outline-none focus:ring-2 focus:ring-purple-500/20"
                          >
                            <option value="">Promote this listing…</option>
                            <option value="standard">Standard — £5</option>
                            <option value="promoted">Promoted — £10</option>
                            <option value="premium">Premium/Featured — £25</option>
                          </select>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Enrollments Tab */}
            {activeTab === 'enrollments' && (
              <Card>
                <CardHeader>
                  <CardTitle>All Enrollments ({dashboard?.total_enrollments || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? <Skeleton className="h-48" /> : (dashboard?.recent_enrollments || []).length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No enrollments yet. Promote your courses to attract students.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(dashboard?.recent_enrollments || []).map((enr, i) => (
                        <div key={i} className="flex items-center justify-between p-4 rounded-lg border hover:border-emerald-200 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                              <Users className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">Student ID: {enr.student_id?.slice(0, 8)}...</p>
                              <p className="text-xs text-muted-foreground">
                                Course: {enr.course_id?.slice(0, 8)}... • Enrolled: {enr.enrolled_at?.slice(0, 10)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-sm text-muted-foreground">
                              Progress: <span className="font-medium text-foreground">{enr.progress || 0}%</span>
                            </div>
                            <Badge className={
                              enr.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                              enr.status === 'active' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {enr.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>

      {/* Edit Course Modal */}
      {showEditModal && editForm && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Course</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea rows={4} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Instructor</Label>
                  <Input value={editForm.instructor} onChange={(e) => setEditForm({ ...editForm, instructor: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Input value={editForm.duration} onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Certification</Label>
                  <Input value={editForm.certification} onChange={(e) => setEditForm({ ...editForm, certification: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <select
                    value={editForm.currency}
                    onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Price</Label>
                  <Input type="number" step="0.01" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Discount from price</Label>
                  <Input type="number" step="0.01" value={editForm.original_price} onChange={(e) => setEditForm({ ...editForm, original_price: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Mode</Label>
                  <select
                    value={editForm.delivery_mode}
                    onChange={(e) => setEditForm({ ...editForm, delivery_mode: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                  >
                    <option value="online">Online</option>
                    <option value="in_person">In Person</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} disabled={editForm.delivery_mode === 'online'} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Image URL</Label>
                <Input value={editForm.image_url} onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Video URL</Label>
                <Input value={editForm.video_url} onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Requirements (one per line)</Label>
                <Textarea rows={3} value={editForm.requirements} onChange={(e) => setEditForm({ ...editForm, requirements: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Skills Gained / Outcomes (one per line)</Label>
                <Textarea rows={3} value={editForm.outcomes} onChange={(e) => setEditForm({ ...editForm, outcomes: e.target.value })} />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button onClick={saveEditedCourse} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700">
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
